import classNames from "classnames";
import React, { forwardRef, useRef, useState } from "react";
import useSlidingIndicator from "../../../shared/hooks/useSlidingIndicator";
import KuikaSwap from "../kuika-swap/kuika-swap";
import "./kuika-otp-input.scss";

export type KuikaOtpInputStatus = "default" | "error";

export interface IKuikaOtpInputProps {
  /** The code typed so far, digits only. Controlled when given. */
  value?: string;
  defaultValue?: string;
  /** Every edit, with the code as a digit string (non-digits are dropped before it is reported). */
  onChange?: (value: string) => void;
  /** When an edit leaves every cell filled — once per new code, including one pasted over a full field. */
  onComplete?: (value: string) => void;
  /** How many digits the code has. */
  length?: number;
  /** `error` draws the cells red and shakes the row once as it turns on; clear it on the next edit to shake again. */
  status?: KuikaOtpInputStatus;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Accessible name of the field; the cells are drawing only. */
  ariaLabel?: string;
  id?: string;
  name?: string;
  dataTestId?: string;
  className?: string;
}

/** A cell's place in a pasted code's fill order, read by the stylesheet as an entrance delay. */
interface IOtpCellStyle extends React.CSSProperties {
  "--otp-stagger": number;
}

/** Where the last edit started and whether it took digits away. */
interface IOtpEdit {
  start: number;
  removed: boolean;
}

const NON_DIGITS: RegExp = /\D/g;

// ASCII digits only, whatever the locale: `\D` is [^0-9] in JS.
const toDigits = (text: string, length: number): string => text.replace(NON_DIGITS, "").slice(0, length);

const firstDifference = (previous: string, next: string): number => {
  let index: number = 0;
  while (index < previous.length && index < next.length && previous[index] === next[index]) {
    index++;
  }
  return index;
};

/**
 * A one-time code field: one real `<input>` (`autocomplete="one-time-code"`, numeric keyboard), laid
 * transparently over a row of cells that draw the digits.
 *
 * - One input, so the browser's and the phone's code autofill, paste, undo and a screen reader all see
 *   one field. The caret stays at the end: typing appends, Backspace removes the last digit.
 * - A digit rolls into its cell (`KuikaSwap`), a deleted one rolls out downward. A pasted code is
 *   committed at once — `onChange`/`onComplete` fire immediately — while its cells fill one after
 *   another; under reduced motion they fill together.
 * - The focus ring slides from cell to cell (`useSlidingIndicator`) and fades out on blur.
 * - `status="error"` shakes the row once (`kuika-shake`) as it turns on.
 */
const KuikaOtpInput = forwardRef<HTMLInputElement, IKuikaOtpInputProps>(
  (
    {
      value: controlledValue,
      defaultValue = "",
      onChange,
      onComplete,
      length = 6,
      status = "default",
      disabled = false,
      autoFocus = false,
      ariaLabel = "Verification code",
      id,
      name,
      dataTestId,
      className
    }: IKuikaOtpInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ): JSX.Element => {
    const [internalValue, setInternalValue] = useState<string>(toDigits(defaultValue, length));
    const isControlled: boolean = controlledValue !== undefined;
    const value: string = isControlled ? toDigits(controlledValue ?? "", length) : internalValue;
    const [isFocused, setIsFocused] = useState<boolean>(false);

    // The last edit, derived from the previous value during render (React's documented pattern): it
    // tells each cell which way its digit rolls and when its turn comes in a pasted code.
    const [previousValue, setPreviousValue] = useState<string>(value);
    const [lastEdit, setLastEdit] = useState<IOtpEdit>({ start: length, removed: false });
    if (value !== previousValue) {
      setPreviousValue(value);
      setLastEdit({ start: firstDifference(previousValue, value), removed: value.length < previousValue.length });
    }

    // The shake plays as the status turns to error, not on a first render that is already in error.
    const [previousStatus, setPreviousStatus] = useState<KuikaOtpInputStatus>(status);
    const [isShaking, setIsShaking] = useState<boolean>(false);
    if (status !== previousStatus) {
      setPreviousStatus(status);
      setIsShaking(status === "error");
    }

    const cellsRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const activeIndex: number = Math.min(value.length, length - 1);
    useSlidingIndicator(cellsRef, isFocused && !disabled ? activeIndex : null);

    const commit = (next: string): void => {
      if (next === value) {
        return;
      }
      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(next);
      // Every edit that leaves a full code behind is a new code to check — including a whole code
      // pasted over a full one (a wrong code replaced by the right one). An unchanged value never
      // gets here.
      if (next.length === length) {
        onComplete?.(next);
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      commit(toDigits(event.target.value, length));
    };

    // A whole code replaces what was there, wherever the caret was; a fragment is appended.
    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
      event.preventDefault();
      const pasted: string = toDigits(event.clipboardData.getData("text"), length);
      if (pasted.length === 0) {
        return;
      }
      commit(pasted.length === length ? pasted : toDigits(value + pasted, length));
    };

    // The caret lives at the end; the cells, not the caret, show where the next digit goes.
    const handleSelect = (event: React.SyntheticEvent<HTMLInputElement>): void => {
      const input: HTMLInputElement = event.currentTarget;
      const end: number = input.value.length;
      if (input.selectionStart !== end || input.selectionEnd !== end) {
        input.setSelectionRange(end, end);
      }
    };

    const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>): void => {
      // The digits' own rolls bubble here too; only the row's shake ends it.
      if (event.target === event.currentTarget) {
        setIsShaking(false);
      }
    };

    const renderCell = (index: number): JSX.Element => {
      const digit: string = value[index] ?? "";
      const isInLastEdit: boolean = index >= lastEdit.start;
      const cellStyle: IOtpCellStyle = {
        "--otp-stagger": isInLastEdit && !lastEdit.removed ? index - lastEdit.start : 0
      };
      return (
        <span
          key={index}
          className={classNames("kuika-otp-input__cell", { "kuika-otp-input__cell--filled": digit !== "" })}
          data-indicator-key={index}
          style={cellStyle}
        >
          <KuikaSwap contentKey={digit} variant="roll" direction={lastEdit.removed ? "down" : "up"}>
            {digit}
          </KuikaSwap>
        </span>
      );
    };

    const cells: JSX.Element[] = [];
    for (let index = 0; index < length; index++) {
      cells.push(renderCell(index));
    }

    return (
      <div
        className={classNames(
          "kuika-otp-input",
          {
            "kuika-otp-input--error": status === "error",
            "kuika-otp-input--shaking": isShaking,
            "kuika-otp-input--disabled": disabled
          },
          className
        )}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className="kuika-otp-input__cells" ref={cellsRef} aria-hidden="true">
          {cells}
          <span className="kuika-sliding-indicator kuika-otp-input__focus" />
        </div>
        <input
          ref={ref}
          id={id}
          name={name}
          className="kuika-otp-input__field"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          aria-label={ariaLabel}
          aria-invalid={status === "error" || undefined}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          spellCheck={false}
          onChange={handleChange}
          onPaste={handlePaste}
          onSelect={handleSelect}
          onFocus={(): void => setIsFocused(true)}
          onBlur={(): void => setIsFocused(false)}
          data-testid={dataTestId}
        />
      </div>
    );
  }
);

KuikaOtpInput.displayName = "KuikaOtpInput";

export default KuikaOtpInput;
